import { useAudioPlayer } from '@ibsys/kit';
import type { RefObject } from 'react';
import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  CONVERSATION_MESSAGE_PARAM,
  CONVERSATION_PHRASE_PARAM,
  type ConversationTranscript,
  type DialogAudioStatus,
  PHRASE_TYPES,
  useConversationTranscript,
} from '@/entities/conversations';
import { followIntoViewWithinContainer, parseTimeToSeconds } from '@/shared/lib';
import { getScrollAreaViewport } from '../lib/getScrollAreaViewport';
import { highlightCloud } from '../lib/highlightCloud';
import { scrollToTranscriptFragment } from '../lib/scrollToTranscript';
import type { DialogSessionContextValue, EditedInterval, ShowTranscript } from './types';
import { DialogSessionContext } from './context';
import { useIntervalPlayback } from './useIntervalPlayback';

const normalizeTimeToSeconds = (time: string | number): number =>
  typeof time === 'number' ? time : parseTimeToSeconds(time);

const getSpokenPhrases = (transcripts: ConversationTranscript[]): ConversationTranscript[] =>
  transcripts.filter((p) => p.type === PHRASE_TYPES.PHRASE && p.person != null);

/** Реплика, накрывающая указанную секунду записи, либо ближайшая к ней. Границы реплик — в миллисекундах. */
const findPhraseAtTime = (
  spokenPhrases: ConversationTranscript[],
  targetSec: number,
): ConversationTranscript | null => {
  if (spokenPhrases.length === 0) return null;
  const targetMs = targetSec * 1000;
  const containing = spokenPhrases.findLast(
    (p) => p.startedAt <= targetMs && (p.finishedAt ?? p.startedAt) >= targetMs,
  );
  if (containing) return containing;
  return spokenPhrases.reduce((prev, curr) =>
    Math.abs(curr.startedAt - targetMs) < Math.abs(prev.startedAt - targetMs) ? curr : prev,
  );
};

type Props = {
  dialogId: string;
  audioStatus?: DialogAudioStatus;
  /** Повторный запрос записи у интеграции-источника */
  retryRecordingFetch: () => void;
  children: React.ReactNode;
};

export const DialogSession: FC<Props> = ({ dialogId, audioStatus = 'ready', retryRecordingFetch, children }) => {
  const [transcriptContainerRef, setTranscriptContainerRef] = useState<RefObject<HTMLElement | null> | null>(null);
  const [autoPlayOnScroll, setAutoPlayOnScroll] = useState(false);
  // По умолчанию транскрипт показывается анонимизированным; неанонимизированный вид — по явному переключению.
  const [showNonAnonymized, setShowNonAnonymized] = useState(false);
  const [editedInterval, setEditedInterval] = useState<EditedInterval | null>(null);

  const transcriptsQueryResult = useConversationTranscript(dialogId, !showNonAnonymized);
  const transcripts = transcriptsQueryResult.data ?? [];
  const audioPlayer = useAudioPlayer();
  const { state, actions } = audioPlayer;
  const { playingInterval, playInterval } = useIntervalPlayback(audioPlayer);

  const transcriptsQuery = useMemo<DialogSessionContextValue['transcriptsQuery']>(
    () => ({
      data: transcripts,
      isSuccess: transcriptsQueryResult.isSuccess,
      isLoading: transcriptsQueryResult.isLoading,
    }),
    [transcripts, transcriptsQueryResult.isSuccess, transcriptsQueryResult.isLoading],
  );

  const registerTranscriptContainer = useCallback((ref: RefObject<HTMLElement | null> | null) => {
    setTranscriptContainerRef(ref);
  }, []);

  // Показ переписки, скрытой на своей панели; не зарегистрирован — переписка видна всегда
  const showTranscriptRef = useRef<ShowTranscript | null>(null);

  // Переход к месту переписки: скрытую на своей панели сначала показать, иначе прокручивать некуда
  const jumpInTranscript = useCallback((jump: () => void) => {
    const showTranscript = showTranscriptRef.current;
    if (showTranscript) showTranscript(jump);
    else jump();
  }, []);

  const scrollTranscriptToFragment = useCallback(
    (messageId: string) => {
      const container = transcriptContainerRef?.current ?? undefined;
      scrollToTranscriptFragment(messageId, transcripts, container);
    },
    [transcripts, transcriptContainerRef],
  );

  const scrollToTranscript = useCallback(
    (messageId: string) => jumpInTranscript(() => scrollTranscriptToFragment(messageId)),
    [jumpInTranscript, scrollTranscriptToFragment],
  );

  // Облака элементов переписки по id — цели перехода к сообщению. Облака держит лента переписки,
  // а переход зовут из вкладки «Вложения», поэтому встречаются они здесь
  const messageElementsRef = useRef(new Map<number, HTMLElement>());

  // Снятие удержания облака предыдущего перехода: новый переход уводит ленту к другому облаку
  const stopMessageFollowRef = useRef<(() => void) | null>(null);

  useEffect(() => () => stopMessageFollowRef.current?.(), []);

  // К верхнему краю облака: над ним время отправки, а длинное письмо в середину области не уместилось бы.
  // Облако удерживается, пока лента догружает картинки: только что показанная, она растёт уже во время прокрутки
  const followMessage = useCallback((messageElement: HTMLElement | undefined) => {
    stopMessageFollowRef.current?.();
    stopMessageFollowRef.current = followIntoViewWithinContainer(messageElement, {
      align: 'start',
      behavior: 'smooth',
      container: getScrollAreaViewport(messageElement),
    });
  }, []);

  // Сообщение из условия открытия ?messageId=, к которому ещё не перешли: его облака нет, пока лента
  // не загружена и не показана, — переход выполняет регистрация облака
  const pendingMessageIdRef = useRef<number | null>(null);

  // Сообщение из условия открытия, к которому переходили без показа переписки: раскладка с перепиской
  // на своей панели (узкий экран) появляется уже после первой отрисовки — ширину экрана карточка узнаёт
  // позже. Показ переписки для него ещё не выполнялся
  const unrevealedMessageIdRef = useRef<number | null>(null);

  // Облако берётся в момент перехода: в скрытой панели переписки его ещё нет. Переход к другому
  // сообщению заменяет открытие на найденном — оно больше не перехватит ленту
  const scrollToMessage = useCallback(
    (messageId: number) => {
      pendingMessageIdRef.current = null;
      unrevealedMessageIdRef.current = null;
      jumpInTranscript(() => followMessage(messageElementsRef.current.get(messageId)));
    },
    [jumpInTranscript, followMessage],
  );

  const jumpToPendingMessage = useCallback(() => {
    const messageId = pendingMessageIdRef.current;
    const messageElement = messageId == null ? undefined : messageElementsRef.current.get(messageId);
    if (!messageElement) return;
    pendingMessageIdRef.current = null;
    followMessage(messageElement);
    highlightCloud(messageElement);
  }, [followMessage]);

  const registerMessageElement = useCallback(
    (messageId: number, element: HTMLElement) => {
      const messageElements = messageElementsRef.current;
      messageElements.set(messageId, element);
      if (messageId === pendingMessageIdRef.current) jumpToPendingMessage();
      return () => {
        if (messageElements.get(messageId) === element) messageElements.delete(messageId);
      };
    },
    [jumpToPendingMessage],
  );

  // Первая регистрация показа после открытия на сообщении показывает переписку и переходит к сообщению
  // заново: прежний переход мог пройти в ленте прежней раскладки, которой уже нет
  const registerShowTranscript = useCallback(
    (show: ShowTranscript) => {
      showTranscriptRef.current = show;
      const unrevealedMessageId = unrevealedMessageIdRef.current;
      if (unrevealedMessageId != null) {
        unrevealedMessageIdRef.current = null;
        pendingMessageIdRef.current = unrevealedMessageId;
        show(jumpToPendingMessage);
      }
      return () => {
        if (showTranscriptRef.current === show) showTranscriptRef.current = null;
      };
    },
    [jumpToPendingMessage],
  );

  // Автоскролл к реплике по URL ?phraseId=... после загрузки транскрипта (spec: phrase-search → экран диалога).
  // Срабатывает один раз на конкретное значение phraseId и при готовности контейнера транскрипта.
  const [searchParams] = useSearchParams();
  const phraseIdParam = searchParams.get(CONVERSATION_PHRASE_PARAM);
  const lastScrolledRef = useRef<string | null>(null);
  useEffect(() => {
    if (!phraseIdParam) return;
    if (!transcriptsQueryResult.isSuccess || transcripts.length === 0) return;
    if (!transcriptContainerRef?.current) return;
    if (lastScrolledRef.current === phraseIdParam) return;
    lastScrolledRef.current = phraseIdParam;
    scrollToTranscriptFragment(phraseIdParam, transcripts, transcriptContainerRef.current);
  }, [phraseIdParam, transcripts, transcriptsQueryResult.isSuccess, transcriptContainerRef]);

  // Открытие текстовой коммуникации на найденном сообщении: один раз на значение ?messageId= — прочие
  // зависимости постоянны. Скрытая на своей панели переписка сначала показывается — иначе облако не появится.
  // Повторный запуск после снятия эффекта (StrictMode в разработке) переходит заново: удержание облака
  // снимается вместе с карточкой, и без нового перехода облако уехало бы, пока лента догружается
  const messageIdParam = searchParams.get(CONVERSATION_MESSAGE_PARAM);
  useEffect(() => {
    const messageId = Number(messageIdParam);
    if (!messageIdParam || Number.isNaN(messageId)) return;
    pendingMessageIdRef.current = messageId;
    unrevealedMessageIdRef.current = showTranscriptRef.current ? null : messageId;
    jumpInTranscript(jumpToPendingMessage);
    return () => {
      pendingMessageIdRef.current = null;
      unrevealedMessageIdRef.current = null;
    };
  }, [messageIdParam, jumpInTranscript, jumpToPendingMessage]);

  const playFragment = useCallback(
    (fragmentId: string, options?: { scrollTo?: boolean }) => {
      const { scrollTo = true } = options || {};

      const phraseId = Number(fragmentId);
      if (Number.isNaN(phraseId)) return;

      const phrase = transcripts.find((p) => p.id === phraseId);
      if (!phrase) return;

      // Границы реплики приходят в миллисекундах, плеер принимает секунды
      const startSec = phrase.startedAt / 1000;
      actions.seek(startSec);
      actions.play();
      // Воспроизведение панель переписки не переключает — только прокручивает видимую
      if (scrollTo) {
        scrollTranscriptToFragment(fragmentId);
      }
    },
    [transcripts, actions, scrollTranscriptToFragment],
  );

  const playAtTime = useCallback(
    (time: string | number, options?: { scrollTo?: boolean }) => {
      const spokenPhrases = getSpokenPhrases(transcripts);
      const phrase = findPhraseAtTime(spokenPhrases, normalizeTimeToSeconds(time));
      if (phrase) playFragment(String(phrase.id), options);
    },
    [transcripts, playFragment],
  );

  const scrollToTime = useCallback(
    (time: string | number, options?: { autoPlay?: boolean }) => {
      const targetSec = normalizeTimeToSeconds(time);
      const spokenPhrases = getSpokenPhrases(transcripts);
      const phrase = findPhraseAtTime(spokenPhrases, targetSec);
      if (!phrase) return;

      const container = transcriptContainerRef?.current ?? undefined;
      scrollToTranscriptFragment(String(phrase.id), transcripts, container);

      const shouldAutoPlay = options?.autoPlay ?? autoPlayOnScroll;
      if (shouldAutoPlay) {
        actions.seek(targetSec);
        actions.play();
      }
    },
    [transcripts, transcriptContainerRef, autoPlayOnScroll, actions],
  );

  const contextValue = useMemo<DialogSessionContextValue>(
    () => ({
      transcriptsQuery,
      transcripts,
      duration: state.duration,
      isPlaying: state.isPlaying,
      audioStatus,
      retryRecordingFetch,
      autoPlayOnScroll,
      setAutoPlayOnScroll,
      playFragment,
      playAtTime,
      scrollToTime,
      scrollToTranscript,
      scrollToMessage,
      registerMessageElement,
      registerShowTranscript,
      registerTranscriptContainer,
      pause: actions.pause,
      play: actions.play,
      seek: actions.seek,
      showNonAnonymized,
      setShowNonAnonymized,
      editedInterval,
      setEditedInterval,
      playInterval,
      playingInterval,
    }),
    [
      transcriptsQuery,
      transcripts,
      state.duration,
      state.isPlaying,
      audioStatus,
      retryRecordingFetch,
      autoPlayOnScroll,
      playFragment,
      playAtTime,
      scrollToTime,
      scrollToTranscript,
      scrollToMessage,
      registerMessageElement,
      registerShowTranscript,
      registerTranscriptContainer,
      actions.pause,
      actions.play,
      actions.seek,
      showNonAnonymized,
      editedInterval,
      playInterval,
      playingInterval,
    ],
  );

  return <DialogSessionContext.Provider value={contextValue}>{children}</DialogSessionContext.Provider>;
};
